<template>
  <n-config-provider :theme="lightTheme" :theme-overrides="themeOverrides">
    <div class="app" :class="theme">
      <n-notification-provider>
        <Header v-if="!hideHeader" />
        <main :style="{ marginTop: hideHeader ? '0' : '3.4rem' }">
          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>
        </main>
        <Footer v-if="!hideFooter" />
      </n-notification-provider>
    </div>
  </n-config-provider>
</template>

<script setup lang="ts">
import {
  computed,
  watch,
  onBeforeMount,
  onMounted,
  onBeforeUpdate,
  onUpdated,
  onBeforeUnmount,
  onUnmounted,
} from 'vue'
import { useRoute } from 'vue-router'
import { useSiteStore } from '@/store/modules/useSiteStore'
import { useUserStore } from '@/store/modules/useUserStore'
import Header from '@/components/layout/Header.vue'
import Footer from '@/components/layout/Footer.vue'
import {
  NConfigProvider,
  NNotificationProvider,
  darkTheme,
  lightTheme,
  GlobalThemeOverrides,
} from 'naive-ui'

const { refreshToken } = useUserStore(),
  siteStore = useSiteStore(),
  route = useRoute(),
  theme = computed(() => siteStore.theme),
  hideHeader = computed(() => route.meta.hideHeader || false),
  hideFooter = computed(() => route.meta.hideFooter || false),
  themeOverrides: GlobalThemeOverrides = {
    common: {
      primaryColor: '#2563eb',
      primaryColorHover: '#3a75ed',
      primaryColorPressed: '#1a56d6',
    },
    Button: {
      colorPrimary: '#2563eb',
      colorHoverPrimary: '#3a75ed',
      colorPressedPrimary: '#1a56d6',
      colorFocusPrimary: '#2563eb',
    },
    Spin: {
      fontSize: '1rem',
      textColor: '#606266',
      sizeTiny: '12px',
      sizeSmall: '16px',
      sizeMedium: '20px',
      sizeLarge: '24px',
      sizeHuge: '28',
      color: '#606266',
      opacitySpinning: '0.5',
    },
    Collapse: {
      titleFontSize: '1.2rem',
      titleFontWeight: '400',
      dividerColor: '#E4E7ED',
      titleTextColor: '#303133',
      titleTextColorDisabled: '#9d9d9d',
      fontSize: '1rem',
      textColor: '#606266',
      arrowColor: '#303133',
      arrowColorDisabled: 'transparent',
      itemMargin: '18px 12px',
      // titlePadding: '12px 12px',
    },
    Input: {
      border: '1px #cdcdcd solid',
      // borderHover: '1px #2563eb solid',
      // borderFocus: '1px #2563eb solid',
      // boxShadowFocus: '0 0 2px 2px rgba(0, 0, 0, 0.2)',
    },
    Pagination: {
      itemTextColor: '#2563eb',
      itemTextColorHover: '#2563eb',
      itemTextColorPressed: '#ffffff',
      itemTextColorActive: '#ffffff',
      itemColor: 'transparent',
      itemColorHover: 'transparent',
      itemColorPressed: '#2563eb',
      itemColorActive: '#2563eb',
      itemColorActiveHover: '#2563eb',
      itemBorder: '1px #e0e0e7 solid',
      itemBorderHover: '1px #2563eb solid',
      itemBorderPressed: '1px #2563eb solid',
      itemBorderActive: '1px #2563eb solid',
    },
    Slider: {
      railColor: '#cdcdcd',
      railColorHover: '#cdcdcd',
      fillColor: '#2563eb',
      fillColorHover: '#2563eb',
    },
    Marquee: {
      speed: 100,
    }
  }

onBeforeMount(async () => {
  // console.log('DOM 挂载前')
  await refreshToken()
})

onMounted(() => {
  // 初始化数据、访问DOM、启动定时器、调用API
  // console.log('DOM 已挂载') // 最常用的钩子
})

onBeforeUpdate(() => {
  // console.log('数据变化，DOM 更新前')
})

onUpdated(() => {
  // 避免在此修改状态！
  // console.log('DOM 已更新')
})

onBeforeUnmount(() => {
  // console.log('实例销毁前')
})

onUnmounted(() => {
  // console.log('实例已销毁') // 必须在此清理定时器/事件监听
})
</script>

<style lang="scss">
.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--bg-color-base);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.full-screen-loading {
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.4);
  overflow: hidden;
  z-index: 9;
  font-size: 1.2rem;
  font-style: italic;
  color: var(--color-primary);

  div {
    width: 36px;
    height: 36px;
    border: 2px solid var(--color-primary);
    border-radius: 50%;
    border-top-color: transparent;
    animation: spin 1s ease-in-out infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}

.partial-loading {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.4);
  color: #f5f5f5;
  font-size: 0.8rem;
  font-style: italic;

  div {
    width: 24px;
    height: 24px;
    border: 2px solid #9d9d9d;
    border-radius: 50%;
    border-top-color: #f5f5f5;
    animation: spin 1s ease-in-out infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
